import AddIcon from '@mui/icons-material/Add';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '../../../../../context/auth';
import { errorToast, successToast } from '../../../../Toast';
import { TenantsApi } from '../../../namespace/api';
import IntegrationsAPI, {
	ExternalServiceIntegration,
	ExternalServiceIntegrationAddRequest,
	ExternalServiceIntegrationUpdateRequest,
	QUERY_KEYS as INTEGRATION_QUERY_KEYS,
} from '../../api/integrations';
import {
	QUERY_KEYS,
	useWebhookConfigurations,
	WebhookResponse,
} from '../../api/webhooks';
import {
	ADD_INTEGRATION_FAILURE,
	ADD_INTEGRATION_SUCCESS,
	CLOSE_TEXT,
	CREATE_FIRST_DESCRIPTION,
	CREATE_FIRST_TITLE,
	DELETE_INTEGRATION_FAILURE,
	DELETE_INTEGRATION_SUCCESS,
	EDIT_INTEGRATION_FAILURE,
	EDIT_INTEGRATION_SUCCESS,
	NEW_LABEL,
	WEBHOOKS_CONFIGURED_LABEL,
	WEBHOOK_CONFIGURATION_LABEL,
	WEBHOOK_INTEGRATION_LABEL,
	WEBHOOK_LABEL,
	WEBHOOK_TYPE,
} from '../../data/constants';
import IntegrationCard from './IntegrationCard';
import IntegrationSummary from './IntegrationSummary';
import AddUpdateIntegrationModal from './modals/AddUpdateIntegrationModal';
import ConfirmDeleteIntegrationModal from './modals/ConfirmDeleteIntegrationModal';
import WebhookConfigurationModal from './modals/WebhookConfigurationModal';
import WebhooksConfigurationsTable from './WebhooksConfigurationsTable';

export default function WebhooksPanels() {
	const { tenantHostname } = useParams();
	const client = useQueryClient();
	const authData = useAuthContext();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const authTenantId = authData?.tenantAuthorizationData?.tenantId;
	const queryKey = [INTEGRATION_QUERY_KEYS.WEBHOOKS, authTenantId];
	const { data: webhooks } = IntegrationsAPI.useWebhookIntegrations(
		queryKey,
		authToken,
		tenantHostname,
	);
	const { data: configuredWebhooks } = useWebhookConfigurations(
		[QUERY_KEYS.WEBHOOK_CONFIGURATIONS, authTenantId],
		authToken,
		tenantHostname,
	);

	const [showIntegrationModal, setShowIntegrationModal] = useState(false);
	const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
	const [selectedIntegration, setSelectedIntegration] =
		useState<ExternalServiceIntegration>(null);

	const [showWebhookConfigurationModal, setShowWebhookConfigurationModal] =
		useState(false);
	const [selectedWebhookConfiguration, setSelectedWebhookConfiguration] =
		useState<WebhookResponse>(null);
	const [
		showWarningNoWebhookIntegrationModal,
		setShowWarningNoWebhookIntegrationModal,
	] = useState(false);

	const { mutate: addWebhook, isLoading: isAddingWebhook } = useMutation(
		(newWebhook: ExternalServiceIntegrationAddRequest) => {
			return tenantHostname
				? TenantsApi.addIntegration(newWebhook, tenantHostname)
				: IntegrationsAPI.addIntegration(newWebhook, authToken);
		},
		{
			onSuccess: async () => {
				await client.invalidateQueries(queryKey);
				successToast(ADD_INTEGRATION_SUCCESS());
				setShowIntegrationModal(false);
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err?.response?.data?.message) {
					errorToast(
						ADD_INTEGRATION_FAILURE() + ': ' + err.response.data.message,
					);
				} else {
					errorToast(ADD_INTEGRATION_FAILURE());
				}
			},
		},
	);

	const { mutate: updateWebhook, isLoading: isUpdatingWebhook } = useMutation(
		(updatedWebhook: ExternalServiceIntegrationUpdateRequest) => {
			return tenantHostname
				? TenantsApi.editIntegration(
						updatedWebhook,
						selectedIntegration.name,
						tenantHostname,
				  )
				: IntegrationsAPI.editIntegration(
						updatedWebhook,
						selectedIntegration.name,
						authToken,
				  );
		},
		{
			onSuccess: async () => {
				await client.invalidateQueries(queryKey);
				successToast(EDIT_INTEGRATION_SUCCESS());
				setShowIntegrationModal(false);
				setSelectedIntegration(null);
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast(
						EDIT_INTEGRATION_FAILURE() + ': ' + err.response.data.message,
					);
				} else {
					errorToast(EDIT_INTEGRATION_FAILURE());
				}
			},
		},
	);

	const { mutate: removeWebhook, isLoading: isRemovingWebhook } = useMutation(
		() => {
			return tenantHostname
				? TenantsApi.deleteIntegration(selectedIntegration.name, tenantHostname)
				: IntegrationsAPI.deleteIntegration(
						selectedIntegration.name,
						authToken,
				  );
		},
		{
			onSuccess: async () => {
				await client.invalidateQueries(queryKey);
				successToast(DELETE_INTEGRATION_SUCCESS());
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err?.response?.data?.message) {
					errorToast(
						DELETE_INTEGRATION_FAILURE() + ': ' + err.response.data.message,
					);
				} else {
					errorToast(DELETE_INTEGRATION_FAILURE());
				}
			},
			onSettled: () => {
				setShowConfirmDeleteModal(false);
				setSelectedIntegration(null);
			},
		},
	);

	const isLoading = isAddingWebhook || isUpdatingWebhook || isRemovingWebhook;

	const handleAddIntegration = async (
		integration: ExternalServiceIntegration,
	) => {
		addWebhook(integration);
	};

	const handleEditIntegration = (integration: ExternalServiceIntegration) => {
		updateWebhook(integration);
	};

	const handleDeleteIntegration = () => {
		removeWebhook();
	};

	const handleOpenDeleteIntegrationModal = (
		integration: ExternalServiceIntegration,
	) => {
		setSelectedIntegration(integration);
		setShowConfirmDeleteModal(true);
	};

	const handleOpenEditIntegrationModal = (
		integration: ExternalServiceIntegration,
	) => {
		setSelectedIntegration(integration);
		setShowIntegrationModal(true);
	};

	const onCancel = () => {
		setSelectedIntegration(null);
		setShowIntegrationModal(false);
		setShowConfirmDeleteModal(false);
	};

	const handleCloseWebhookConfigurationModal = () => {
		setShowWebhookConfigurationModal(false);
	};

	const handleSuccessWebhookConfiguration = async () => {
		setShowWebhookConfigurationModal(false);
		setSelectedWebhookConfiguration(null);
	};

	const handleOpenEditWebhookConfigurationModal = (
		configuredWebhook: WebhookResponse,
	) => {
		setSelectedWebhookConfiguration(configuredWebhook);
		setShowWebhookConfigurationModal(true);
	};

	const handleOpenAddIntegrationModal = () => {
		setSelectedIntegration(null);
		setShowIntegrationModal(true);
	};

	const handleOpenWebhookConfigurationModal = () => {
		if (webhooks?.length) {
			setSelectedWebhookConfiguration(null);
			setShowWebhookConfigurationModal(true);
		} else {
			setShowWarningNoWebhookIntegrationModal(true);
		}
	};

	return (
		<>
			<Box
				sx={{
					height: '100%',
					display: 'grid',
					gap: 2,
					gridTemplateColumns: '266px 1fr',
					overflow: 'hidden',
					pb: 2,
				}}
			>
				<Box sx={{ flex: 1, overflowY: 'auto' }}>
					<Stack direction='column' spacing={2} sx={{ maxHeight: '100%' }}>
						<Button
							variant='contained'
							color='primary'
							sx={{ width: 'fit-content' }}
							startIcon={<AddIcon />}
							onClick={handleOpenAddIntegrationModal}
						>
							{NEW_LABEL(WEBHOOK_INTEGRATION_LABEL)}
						</Button>
						<IntegrationSummary
							title={WEBHOOK_LABEL(true)}
							count={webhooks?.length ?? 0}
						/>
						{showIntegrationModal ? (
							<AddUpdateIntegrationModal
								open={showIntegrationModal}
								integrationNames={
									webhooks ? webhooks.map((webhook) => webhook.name) : []
								}
								integrationType={WEBHOOK_TYPE}
								integrationTypeLabel={WEBHOOK_LABEL}
								integration={selectedIntegration}
								onCancel={onCancel}
								onAdd={handleAddIntegration}
								onEdit={handleEditIntegration}
								isLoading={isLoading}
							/>
						) : null}
						{showConfirmDeleteModal ? (
							<ConfirmDeleteIntegrationModal
								open={showConfirmDeleteModal}
								integrationTypeLabel={WEBHOOK_TYPE}
								integration={selectedIntegration}
								onCancel={onCancel}
								onDelete={handleDeleteIntegration}
							/>
						) : null}
						{webhooks?.map((integration, i) => (
							<IntegrationCard
								integration={integration}
								key={WEBHOOK_TYPE() + i}
								onEdit={handleOpenEditIntegrationModal}
								onDelete={handleOpenDeleteIntegrationModal}
							/>
						))}
					</Stack>
				</Box>
				<Stack
					flex={1}
					height='100%'
					direction='column'
					spacing={2}
					id='Integration__WebhooksPanelWebhooksConfigurationContainer'
					sx={{ overflowY: 'auto' }}
				>
					<Button
						variant='contained'
						color='primary'
						sx={{ width: 'fit-content' }}
						startIcon={<AddIcon />}
						onClick={handleOpenWebhookConfigurationModal}
					>
						{NEW_LABEL(WEBHOOK_CONFIGURATION_LABEL)}
					</Button>
					<IntegrationSummary
						width={266}
						title={WEBHOOKS_CONFIGURED_LABEL()}
						count={configuredWebhooks?.length ?? 0}
					/>
					{showWebhookConfigurationModal && (
						<WebhookConfigurationModal
							open={showWebhookConfigurationModal}
							webhooks={webhooks}
							onModalClose={handleCloseWebhookConfigurationModal}
							onSuccess={handleSuccessWebhookConfiguration}
							configuredWebhook={selectedWebhookConfiguration}
							configuredWebhooks={configuredWebhooks}
							tenantHostname={tenantHostname}
						/>
					)}
					{configuredWebhooks?.length ? (
						<WebhooksConfigurationsTable
							tenantHostname={tenantHostname}
							configuredWebhooks={configuredWebhooks}
							onEdit={handleOpenEditWebhookConfigurationModal}
						/>
					) : null}
				</Stack>
			</Box>
			{showWarningNoWebhookIntegrationModal ? (
				<Dialog open={showWarningNoWebhookIntegrationModal}>
					<DialogTitle>{CREATE_FIRST_TITLE(WEBHOOK_LABEL)}</DialogTitle>
					<DialogContent>
						{CREATE_FIRST_DESCRIPTION(
							WEBHOOK_LABEL,
							WEBHOOK_CONFIGURATION_LABEL,
						)}
					</DialogContent>
					<DialogActions sx={{ m: 2 }}>
						<Button
							color='inherit'
							onClick={() => {
								setShowWarningNoWebhookIntegrationModal(false);
							}}
						>
							{CLOSE_TEXT()}
						</Button>
						<Button
							color='secondary'
							variant='contained'
							onClick={() => {
								setShowWarningNoWebhookIntegrationModal(false);
								handleOpenAddIntegrationModal();
							}}
						>
							{NEW_LABEL(WEBHOOK_LABEL)}
						</Button>
					</DialogActions>
				</Dialog>
			) : null}
		</>
	);
}
